import React from 'react';
import type { SwitchProps, SxProps, Theme } from '@mui/material';
import { FormControlLabel } from '@mui/material';

import { StyledSwitch } from './Switch.styles';

export interface CustomSwitchProps extends SwitchProps {
  /**
   * Optional label to display alongside the switch
   */
  label?: string;
  /**
   * Position of the label relative to the switch
   * @default 'end'
   */
  labelPlacement?: 'end' | 'start' | 'top' | 'bottom';
  /**
   * Custom sx props for the wrapper FormControlLabel
   */
  sx?: SxProps<Theme>;
}

/**
 * iOS-style Switch component with consistent styling
 * Uses theme primary color when checked
 * Maintains iOS dimensions but supports MUI size prop
 */
export const Switch: React.FC<CustomSwitchProps> = ({
  size = 'medium',
  label,
  labelPlacement = 'end',
  disabled = false,
  sx,
  ...props
}) => {
  // If no label, render switch directly
  if (!label) {
    return (
      <StyledSwitch
        focusVisibleClassName=".Mui-focusVisible"
        disableRipple
        size={size}
        disabled={disabled}
        {...props}
      />
    );
  }

  // If label provided, wrap in FormControlLabel
  return (
    <FormControlLabel
      control={
        <StyledSwitch
          focusVisibleClassName=".Mui-focusVisible"
          disableRipple
          size={size}
          disabled={disabled}
          {...props}
        />
      }
      label={label}
      labelPlacement={labelPlacement}
      disabled={disabled}
      sx={{
        marginLeft: 0,
        marginRight: 0,
        gap: 1.5, // 12px spacing between switch and label
        // eslint-disable-next-line @typescript-eslint/no-misused-spread
        ...(sx || {}),
      }}
    />
  );
};
